import { fetchJsonSettingFromSupabase, fetchPublicRoomsFromSupabase, fetchRoomTypeAvailability } from './publicSupabase'
import { getApiUrl } from '../utils/api'

export interface PublicPaymentSettings {
  yocoPublicKey: string
  eftBankName: string
  eftAccountName: string
  eftAccountNumber: string
  eftBranchCode: string
  eftAccountType: string
  eftReferencePrefix: string
  eftInstructions: string
}

export interface EftBookingInput {
  bookingReference: string
  roomTypeId: number
  checkIn: string
  checkOut: string
  guests: number
  baseRate: number
  adminFee: number
  breakageDeposit: number
  totalAmount: number
  guestName: string
  guestEmail: string
  guestPhone?: string
  specialRequests?: string
  bookingSource?: string
}

export interface EftBookingResult {
  bookingReference: string
  bookingStatus: string
  paymentStatus: string
  guestEmail: string
  emailWarning: string
}

export interface YocoServerCheckoutStatus {
  available: boolean
  publicKeyConfigured: boolean
}

const viteYocoPublicKey = import.meta.env.VITE_YOCO_PUBLIC_KEY || ''

const defaultPaymentSettings: PublicPaymentSettings = {
  yocoPublicKey: viteYocoPublicKey,
  eftBankName: 'Standard Bank',
  eftAccountName: 'Marina Glen Holiday Resort',
  eftAccountNumber: '084629451',
  eftBranchCode: '051001',
  eftAccountType: 'Business Cheque',
  eftReferencePrefix: 'MG',
  eftInstructions: 'Use your booking reference when making payment and upload proof of payment.',
}

const normalizeDate = (value: string) => String(value || '').split('T')[0]

const getJohannesburgDateString = (date = new Date()) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Africa/Johannesburg',
}).format(date)

export const isYocoServerCheckoutEnabled = (): boolean => {
  return String(import.meta.env.VITE_YOCO_SERVER_CHECKOUT_ENABLED || '').toLowerCase() === 'true'
}

export const fetchYocoServerCheckoutStatus = async (): Promise<YocoServerCheckoutStatus> => {
  const response = await fetch(`${getApiUrl()}/api/payments/yoco-checkout`)
  const result = await response.json().catch(() => null)

  if (!response.ok) {
    throw new Error(result?.error || 'Failed to load Yoco checkout status.')
  }

  return {
    available: Boolean(result?.data?.available),
    publicKeyConfigured: Boolean(result?.data?.publicKeyConfigured),
  }
}

export const fetchPublicPaymentSettingsFromSupabase = async (): Promise<PublicPaymentSettings> => {
  const settings = await fetchJsonSettingFromSupabase<PublicPaymentSettings>('payment_gateway_settings', defaultPaymentSettings)

  return {
    ...defaultPaymentSettings,
    ...settings,
    yocoPublicKey: settings.yocoPublicKey || defaultPaymentSettings.yocoPublicKey,
  }
}

export const createEftBookingReservation = async (input: EftBookingInput): Promise<EftBookingResult> => {
  const rooms = await fetchPublicRoomsFromSupabase()

  const selectedRoomType = rooms.find((room) => room.id === input.roomTypeId)
  if (!selectedRoomType) {
    throw new Error('The selected unit type could not be found. Please restart your booking.')
  }

  const requestedNights = Math.max(
    0,
    Math.ceil((new Date(input.checkOut).getTime() - new Date(input.checkIn).getTime()) / (1000 * 3600 * 24))
  )
  if (requestedNights <= 0) {
    throw new Error('Check-out must be after check-in.')
  }

  if (String(input.bookingSource || '') === 'reservation_desk' && normalizeDate(input.checkIn) <= getJohannesburgDateString()) {
    throw new Error('Reservation desk bookings must have an arrival date from tomorrow onward.')
  }

  const minimumStay = Math.max(1, Number(selectedRoomType.pricing?.min_stay ?? 1))
  if (requestedNights < minimumStay) {
    throw new Error(`This unit requires a minimum stay of ${minimumStay} night${minimumStay === 1 ? '' : 's'}.`)
  }

  const availability = await fetchRoomTypeAvailability(input.roomTypeId, input.checkIn, input.checkOut)
  if (!availability.available || availability.availableRooms <= 0) {
    throw new Error(availability.reason || 'This unit is no longer available for your selected dates.')
  }

  const response = await fetch(`${getApiUrl()}/api/bookings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      booking_reference: input.bookingReference,
      room_type_id: input.roomTypeId,
      check_in: normalizeDate(input.checkIn),
      check_out: normalizeDate(input.checkOut),
      nights: requestedNights,
      guests_count: input.guests,
      base_rate: input.baseRate,
      admin_fee: input.adminFee,
      breakage_deposit: input.breakageDeposit,
      total_amount: input.totalAmount,
      status: 'pending',
      payment_status: 'pending',
      payment_method: 'eft',
      special_requests: input.specialRequests?.trim() || null,
      guest_name: input.guestName,
      guest_email: input.guestEmail,
      guest_phone: input.guestPhone?.trim() || null,
      booking_source: input.bookingSource || 'payment_page',
    }),
  })

  const result = await response.json().catch(() => null)

  if (!response.ok) {
    const errorMessage = result?.error || 'Failed to save your EFT booking.'
    if (/already exists/i.test(String(errorMessage))) {
      throw new Error('This booking reference already exists. Please restart your booking and try again.')
    }
    throw new Error(errorMessage)
  }

  const data = result?.data || {}
  const guestWarning = typeof result?.warning === 'string'
    ? result.warning
    : typeof data?.emailDelivery?.warnings?.[0] === 'string'
      ? data.emailDelivery.warnings[0]
      : 'Booking saved. Guest confirmation emails still require the server-side mail service.'

  return {
    bookingReference: String(data?.booking_reference || input.bookingReference),
    bookingStatus: String(data?.status || 'pending'),
    paymentStatus: String(data?.payment_status || 'pending'),
    guestEmail: String(data?.guest_email || input.guestEmail),
    emailWarning: guestWarning,
  }
}